// useFavorite — favorite (heart) state for a single page, backed by the
// bookmarks store. Reads the current state on mount, then toggles optimistically
// with rollback on failure. Powers the subheader Favorite action.
//
// Neutral by default: `isFavorite` stays false until the read resolves, and
// `isLoading` reports whether that initial read is still in flight so the icon
// can render neutral (not "un-favorited") until the truth is known.
import { useCallback, useEffect, useRef, useState } from "react";

import { isBookmarked, toggleBookmark } from "../../services/bookmarksService";
import { subscribeBookmarksChanged } from "../../services/bookmarksEvents";

// Older urls a page was favorited under. Favorites match the exact url string,
// and Towing & Payload's favorite moved from the legacy `/towingPayload` to the
// redesign's Dynamic Page (ES-13709): without this, a favorite saved before the
// move leaves the new page's heart empty, and clicking it saves a second row.
const LEGACY_FAVORITE_URLS = {
  "/pages/towing-payload/main": ["/towingPayload"],
};

const favoriteUrls = (url) => [url, ...(LEGACY_FAVORITE_URLS[url] ?? [])];

// Favorited under the page's url or any url it used to have.
const readFavorite = async (url) => {
  const saved = await Promise.all(
    favoriteUrls(url).map((key) => isBookmarked(key)),
  );
  return saved.some(Boolean);
};

// Favoriting saves the current url only. Un-favoriting removes the page under
// every url it is saved under, so an old row can't keep the heart filled.
// Returns the new state, or null on failure, like `toggleBookmark`.
const writeFavorite = async (bookmark, next) => {
  if (next || !LEGACY_FAVORITE_URLS[bookmark.url]) {
    return toggleBookmark(bookmark);
  }
  const urls = favoriteUrls(bookmark.url);
  const saved = await Promise.all(urls.map((key) => isBookmarked(key)));
  const results = await Promise.all(
    urls
      .filter((_, i) => saved[i])
      .map((key) => toggleBookmark({ ...bookmark, url: key })),
  );
  if (results.includes(null)) return null;
  return results.some(Boolean);
};

export const useFavorite = ({ url, title, subcategory } = {}) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(url));
  const [isToggling, setIsToggling] = useState(false);

  // Guards against setState after unmount and against a slow read for an old
  // url landing after the url has changed.
  const activeUrl = useRef(url);
  // Suppresses external re-reads mid-toggle so an in-flight write can't be
  // clobbered by a stale server read.
  const togglingRef = useRef(false);
  // Set once the user toggles on this url, so a slower initial read that resolves
  // after the toggle can't revert the heart to the stale pre-toggle value.
  const userActedRef = useRef(false);

  useEffect(() => {
    activeUrl.current = url;
    userActedRef.current = false;

    if (!url) {
      setIsFavorite(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    readFavorite(url).then((favorited) => {
      if (cancelled || activeUrl.current !== url) return;
      if (!userActedRef.current) setIsFavorite(favorited);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Keep the heart in sync when the favorite is changed elsewhere (e.g. removed
  // from the favorites nav panel) — re-read the stored state on any bookmark
  // mutation, without needing a page refresh.
  useEffect(() => {
    if (!url) return undefined;
    const refresh = () => {
      if (togglingRef.current) return;
      readFavorite(url).then((favorited) => {
        if (activeUrl.current === url) setIsFavorite(favorited);
      });
    };
    return subscribeBookmarksChanged(refresh);
  }, [url]);

  const toggle = useCallback(async () => {
    if (!url || isToggling) return;

    const previous = isFavorite;
    const next = !previous;

    // Optimistic flip so the heart responds instantly.
    setIsFavorite(next);
    setIsToggling(true);
    togglingRef.current = true;
    userActedRef.current = true;

    const result = await writeFavorite(
      {
        url,
        title: title ?? "",
        subcategory: subcategory ?? "",
      },
      next,
    );

    togglingRef.current = false;
    setIsToggling(false);

    // Ignore a result that came back for a url we've since navigated away from.
    if (activeUrl.current !== url) return;

    // On failure (null) roll back; otherwise trust the server's reported state.
    setIsFavorite(result === null ? previous : result);
  }, [url, title, subcategory, isFavorite, isToggling]);

  return { isFavorite, isLoading, isToggling, toggle };
};
